import moment from 'moment-tz';

import {userJobDispatcher} from 'app/jobs/userJobDispatcher';
import {sweetAPI} from 'app/store/sweet-api';
import {
  createDetailHookReadingFromById,
  createGenericHookReadingFromById,
  createListHookReadingFromById,
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {
  AUTOMATION_RUN_API_URL,
  getTaskUpgradeVersionURL,
  TASKS_API_URL,
  TASKS_CREATE_TRIGGER_API_URL,
  TASKS_MINE_API_URL,
  TASKS_MULTI_ASSIGN_API_URL,
  TASKS_REASSIGN_TASK_API_URL,
  TASKS_REVOKE_TASK_API_URL,
  TASKS_SLIM_API_URL,
  TASKS_TEMPLATES_API_URL,
} from 'app/urls';
import {gt, ngt} from 'app/utils/i18n';
import {TASKS_RTK_TAGS} from 'app/utils/rtk-tags';
import {toOxfordCommaList} from 'app/utils/strings';

import {TaskDashboardLocationParamsType} from './types';
import {
  extractTaskInstanceId,
  myTasksFlattened,
  repeats,
  rruleFromString,
} from './utils';

import type {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';
import type {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import type {ToastMessageOptionsType} from 'app/toasts/slice';
import type {
  CompletedUserJobSerializedType,
  ListResponseType,
  SlimTaskInstanceSerializedType,
  TaskAssignableContentTypes,
  TaskConcreteInstanceSerializedType,
  TaskMineDashboardSerializedType,
  TaskTemplateSerializedType,
  UpdateTaskPayloadType,
  UserJobSerializedType,
  WriteTaskReassignSerializedType,
  WriteTaskTemplateSerializedType,
} from 'sweet-types';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [
      TASKS_RTK_TAGS.SLIMINSTANCE,
      TASKS_RTK_TAGS.INSTANCE,
      TASKS_RTK_TAGS.TEMPLATE,
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      // doesn't use this directly. Use the useSlimTasks hook instead
      getSlimTasks: build.query<
        ListResponseType<SlimTaskInstanceSerializedType>,
        TaskDashboardLocationParamsType
      >({
        query: (params) => ({
          url: TASKS_SLIM_API_URL,
          params,
        }),
        providesTags: providesTagsByURL(TASKS_RTK_TAGS.SLIMINSTANCE),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load the tasks.'),
          })(args, api);
        },
      }),
      getMyTasks: build.query<TaskMineDashboardSerializedType, void>({
        query: () => ({
          url: TASKS_MINE_API_URL,
        }),
        providesTags: (response) => {
          let tags: Array<{
            type: typeof TASKS_RTK_TAGS.SLIMINSTANCE;
            id: string;
          }> = [{type: TASKS_RTK_TAGS.SLIMINSTANCE, id: 'list'}];

          if (response != null) {
            tags = tags.concat(
              myTasksFlattened(response).map((t) => ({
                type: TASKS_RTK_TAGS.SLIMINSTANCE,
                id: t.url,
              }))
            );
          }

          return tags;
        },
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load the tasks.'),
          })(args, api);
        },
      }),
      getTask: build.query<TaskConcreteInstanceSerializedType, number | string>(
        {
          query: (id) => ({
            url: `${TASKS_API_URL}${id}/`,
          }),
          providesTags: (response) => {
            return response != null
              ? [{type: TASKS_RTK_TAGS.INSTANCE, id: response.url}]
              : [];
          },
          onQueryStarted: (args, api) => {
            showLoadingBar(args, api);
            showMessages({
              onError: () => gt('Could not fetch task.'),
            })(args, api);
          },
        }
      ),
      getTaskTemplates: build.query<
        ListResponseType<TaskTemplateSerializedType>,
        TaskTemplateFetchType
      >({
        query: (params) => ({
          url: TASKS_TEMPLATES_API_URL,
          params,
        }),
        providesTags: providesTagsByURL(TASKS_RTK_TAGS.TEMPLATE),
        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch tasks.'),
        }),
      }),
      updateTaskInstance: build.mutation<
        TaskConcreteInstanceSerializedType,
        {url: string; update: UpdateTaskPayloadType}
      >({
        query: ({url, update}) => {
          const taskPayload = [
            'progress',
            'completed',
            'progress_percent',
            'is_completeable',
          ].reduce((payload, field) => {
            payload[field] = update[field];
            return payload;
          }, {} as UpdateTaskPayloadType);

          return {
            url,
            method: 'PATCH',
            body: taskPayload,
          };
        },
        invalidatesTags: (r) => invalidateAllLists(TASKS_RTK_TAGS.INSTANCE, r),
        onQueryStarted: showMessages({
          onError: {
            fallback: () => gt("Couldn't update the task at the moment"),
            byStatus: {
              400: (response) =>
                response?.progress?.[0] ?? response?.completed?.[0],
              403: () =>
                gt(
                  'Could not update task. Only assigned users can modify a task'
                ),
            },
          },
        }),
      }),
      updateTaskVersion: build.mutation<
        TaskConcreteInstanceSerializedType,
        string | number
      >({
        query: (taskId) => ({
          url: getTaskUpgradeVersionURL(taskId),
          method: 'PATCH',
          body: {},
        }),
        invalidatesTags: (r) => invalidateAllLists(TASKS_RTK_TAGS.INSTANCE, r),
        onQueryStarted: showMessages({
          onError: {
            fallback: () =>
              gt("Couldn't upgrade the task version at the moment"),
            byStatus: {
              400: (response) => response?.non_field_errors?.[0],
              403: () =>
                gt(
                  'Could not update task. Only assigned users can modify a task'
                ),
            },
          },
        }),
      }),

      /* task templates */

      createTaskTemplate: build.mutation<
        TaskTemplateSerializedType,
        WriteTaskTemplateSerializedType
      >({
        query: (payload) => ({
          url: TASKS_TEMPLATES_API_URL,
          method: 'POST',
          body: payload,
        }),
        invalidatesTags: (r) => invalidateAllLists(TASKS_RTK_TAGS.TEMPLATE, r),
        onQueryStarted: showMessages({
          onSuccess: (args, {data: task}) => getAssignedTaskNotification(task),
          onError: {
            fallback: (response) =>
              response?.detail ?? gt('Could not create task'),
            byStatus: {
              400: (response) => response?.non_field_errors?.[0],
            },
          },
        }),
      }),

      createMultipleTaskTemplates: build.mutation<
        CompletedUserJobSerializedType,
        WriteTaskTemplateSerializedType
      >({
        queryFn: (
          payload,
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            CompletedUserJobSerializedType,
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: TASKS_MULTI_ASSIGN_API_URL,
                    method: 'POST',
                    body: payload,
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () =>
          invalidateAllLists(TASKS_RTK_TAGS.TEMPLATE, undefined),
        onQueryStarted: showMessages({
          onError: {
            fallback: () => gt('There was an error assigning the tasks'),
            byStatus: {
              400: (response) => response?.non_field_errors?.[0],
            },
          },
          onSuccess: (args, {data: job}) => [
            gt('Tasks assigned'),
            {link: {text: 'View tasks', to: job.result.url!}},
          ],
        }),
      }),

      triggerTaskTemplate: build.mutation<
        {html_url: string; template: TaskTemplateSerializedType},
        {
          task: WriteTaskTemplateSerializedType;
          skipSuccessNotification?: boolean;
        }
      >({
        query: ({task}) => ({
          url: TASKS_CREATE_TRIGGER_API_URL,
          method: 'POST',
          body: task,
        }),
        invalidatesTags: (r) =>
          invalidateAllLists(TASKS_RTK_TAGS.TEMPLATE, r?.template),
        onQueryStarted: showMessages({
          onSuccess: ({skipSuccessNotification}, {data: {template}}) =>
            skipSuccessNotification
              ? null
              : getAssignedTaskNotification(template),
          onError: {
            fallback: (response) =>
              response?.detail ?? gt('Could not create task'),
            byStatus: {
              400: (response) => response?.non_field_errors?.[0],
            },
          },
        }),
      }),

      reassignTaskTemplate: build.mutation<
        TaskTemplateSerializedType,
        {
          instanceURL?: string;
          task: WriteTaskReassignSerializedType;
          until: string;
        }
      >({
        query: ({task, until}) => ({
          url: TASKS_REASSIGN_TASK_API_URL,
          method: 'POST',
          body: {
            ...task,
            rrule: rruleFromString(task.rrule).toString(),
            /* The RRule `until` spec is inclusive. To cancel from task.due
             * onwards we must set the `until` date earlier than task.due */
            until: moment(until).subtract(1, 'second').toISOString(),
          },
        }),
        invalidatesTags: (r, error, {instanceURL}) =>
          invalidateAllLists(TASKS_RTK_TAGS.TEMPLATE, r).concat(
            instanceURL != null
              ? [{type: TASKS_RTK_TAGS.INSTANCE, id: instanceURL}]
              : []
          ),
        onQueryStarted: showMessages({
          onSuccess: (args, {data: task}) =>
            getAssignedTaskNotification(task, (users) =>
              gt('Updated task assigned to %(users)s', {users})
            ),
          onError: {
            fallback: (response) =>
              response?.detail ?? gt('Could not reassign the task'),
            byStatus: {
              400: (response) => response?.non_field_errors?.[0],
            },
          },
        }),
      }),

      cancelTaskTemplate: build.mutation<
        Record<string, never>,
        {url: string; rrule: string; until?: string}
      >({
        query: ({url, until}) => ({
          url: `${url}cancel/`,
          method: 'PATCH',
          body: {
            /* The RRule `until` spec is inclusive. To cancel from task.due
             * onwards we must set the `until` date earlier than task.due */
            until: moment(until).subtract(1, 'second').toISOString(),
          },
        }),
        invalidatesTags: (r) => invalidateAllLists(TASKS_RTK_TAGS.TEMPLATE, r),
        onQueryStarted: showMessages({
          onSuccess: ({rrule, until}) =>
            !repeats(rrule)
              ? gt('Task canceled')
              : until != null
              ? gt('Tasks canceled')
              : gt('Canceled all future tasks'),
          onError: () => gt('Could not cancel the task'),
        }),
      }),

      deleteTaskInstance: build.mutation<Record<string, never>, string>({
        query: (url) => ({
          url,
          method: 'DELETE',
        }),
        invalidatesTags: () => invalidateAllLists(TASKS_RTK_TAGS.INSTANCE, {}),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Task canceled'),
          onError: () => gt('Could not cancel the task'),
        }),
      }),

      revokeTaskTemplate: build.mutation<Record<string, never>, string>({
        query: (url) => ({
          url: TASKS_REVOKE_TASK_API_URL,
          method: 'POST',
          body: {url},
        }),
        invalidatesTags: (r) => invalidateAllLists(TASKS_RTK_TAGS.TEMPLATE, r),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Task permissions revoked'),
          onError: () => gt('Could not revoke task'),
        }),
      }),

      bulkCancelTasks: build.mutation<
        CompletedUserJobSerializedType,
        {taskinstances: Array<string>}
      >({
        queryFn: (
          {taskinstances},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            CompletedUserJobSerializedType,
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${TASKS_API_URL}bulk_cancel/`,
                    method: 'POST',
                    body: {
                      taskinstances,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => invalidateAllLists(TASKS_RTK_TAGS.INSTANCE, {}),
        onQueryStarted: ({taskinstances, ...args}, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () =>
              ngt(
                'The selected task is being cancelled..',
                'The selected tasks are being cancelled...',
                taskinstances.length
              ),
            onSuccess: () => [
              ngt(
                'The selected task has been cancelled.',
                'The selected tasks have been cancelled.',
                taskinstances.length
              ),
              {level: 'success'},
            ],
            onError: () =>
              ngt(
                "There was an error and we couldn't cancel the task at the moment.",
                "There was an error and we couldn't cancel the selected tasks at the moment.",
                taskinstances.length
              ),
          })(args, api);
        },
      }),

      bulkCancelTasksTemplates: build.mutation<
        CompletedUserJobSerializedType,
        {taskinstances: Array<string>}
      >({
        queryFn: (
          {taskinstances},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            CompletedUserJobSerializedType,
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${TASKS_TEMPLATES_API_URL}bulk_cancel/`,
                    method: 'POST',
                    body: {
                      taskinstances,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => invalidateAllLists(TASKS_RTK_TAGS.TEMPLATE, {}),
        onQueryStarted: ({taskinstances, ...args}, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () =>
              ngt(
                'The selected task and all future tasks in its series are being cancelled..',
                'The selected tasks and all future tasks in each series are being cancelled...',
                taskinstances.length
              ),
            onSuccess: () => [
              ngt(
                'The selected task and all future tasks in its series have been cancelled.',
                'The selected tasks and all future tasks in each series have been cancelled.',
                taskinstances.length
              ),
              {level: 'success'},
            ],
            onError: () =>
              ngt(
                "There was an error and we couldn't cancel the task at the moment.",
                "There was an error and we couldn't cancel the tasks at the moment.",
                taskinstances.length
              ),
          })(args, api);
        },
      }),

      bulkRevokeTasksAccess: build.mutation<
        CompletedUserJobSerializedType,
        {tasks: Array<string>}
      >({
        queryFn: (
          {tasks},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            CompletedUserJobSerializedType,
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${TASKS_TEMPLATES_API_URL}bulk_revoke/`,
                    method: 'POST',
                    body: {
                      tasks,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => invalidateAllLists(TASKS_RTK_TAGS.TEMPLATE, {}),
        onQueryStarted: ({tasks, ...args}, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () =>
              ngt(
                'Access to the selected task is being revoked..',
                'Access to the selected tasks are being revoked...',
                tasks.length
              ),
            onSuccess: () => [
              ngt(
                'Access to the selected task was revoked.',
                'Access to the selected tasks was revoked.',
                tasks.length
              ),
              {level: 'success'},
            ],
            onError: () =>
              ngt(
                "There was an error and we couldn't revoke access to the task at the moment.",
                "There was an error and we couldn't revoke access to the tasks at the moment.",
                tasks.length
              ),
          })(args, api);
        },
      }),

      runAutomation: build.mutation<
        CompletedUserJobSerializedType,
        {taskinstance: string; step_id: string}
      >({
        queryFn: (
          payload,
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            CompletedUserJobSerializedType,
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: AUTOMATION_RUN_API_URL,
                    method: 'POST',
                    body: payload,
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      unknown
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () =>
          invalidateAllLists(TASKS_RTK_TAGS.INSTANCE, undefined),
        onQueryStarted: (args, api) => {
          showMessages({
            onError: {
              fallback: () => gt('Unable to run automation.'),
              byStatus: {
                400: (response) =>
                  response && Object.values(response).flat().join(', '),
              },
            },
          })(args, api);
        },
      }),
    }),
  });

export const {
  useCreateTaskTemplateMutation: useCreateTaskTemplate,
  useCreateMultipleTaskTemplatesMutation: useCreateMultipleTaskTemplates,
  useUpdateTaskVersionMutation: useUpdateTaskVersion,
  useGetTaskTemplatesQuery: useTaskTemplates,
  useTriggerTaskTemplateMutation: useTriggerTaskTemplate,
  useReassignTaskTemplateMutation: useReassignTaskTemplate,
  useCancelTaskTemplateMutation: useCancelTaskTemplate,
  useDeleteTaskInstanceMutation: useDeleteTaskInstance,
  useRevokeTaskTemplateMutation: useRevokeTaskTemplate,
  useUpdateTaskInstanceMutation: useUpdateTaskInstance,
  useBulkCancelTasksMutation: useBulkCancelTasks,
  useBulkCancelTasksTemplatesMutation: useBulkCancelTasksSeries,
  useBulkRevokeTasksAccessMutation: useBulkRevokeTasks,
  useRunAutomationMutation: useRunAutomation,
} = extendedAPI;

/**
 * Drop-in replacement for getSlimTasks.useQuery but this one loads the tasks from
 * our normalized key (tasks.slimInstancesById) which is more updated
 */
export const useSlimTasks = createListHookReadingFromById(
  extendedAPI.endpoints.getSlimTasks.useQuery,
  (state) => state.tasks.slimInstancesById,
  extractTaskInstanceId
);

export const useTaskInstance = createDetailHookReadingFromById(
  extendedAPI.endpoints.getTask.useQuery,
  (state) => state.tasks.instancesById,
  extractTaskInstanceId
);

export const useMyTasks = createGenericHookReadingFromById(
  extendedAPI.endpoints.getMyTasks.useQuery,
  (state) => state.tasks.slimInstancesById,
  extractTaskInstanceId,
  (data, byId, idGetter) => {
    if (data == null) {
      return null;
    }

    return Object.keys(data).reduce((response, key) => {
      response[key] = data[key].map((t) => byId[idGetter(t)]);
      return response;
    }, {});
  }
);

/* auxiliary */

function invalidateAllLists<I extends {url?: string}>(
  type: typeof TASKS_RTK_TAGS[keyof typeof TASKS_RTK_TAGS],
  instance?: I
) {
  if (instance == null) {
    return [];
  }
  return (
    [
      {type: TASKS_RTK_TAGS.SLIMINSTANCE, id: 'list'},
      {type: TASKS_RTK_TAGS.INSTANCE, id: 'list'},
      {type: TASKS_RTK_TAGS.TEMPLATE, id: 'list'},
    ] as Array<{
      type: typeof TASKS_RTK_TAGS[keyof typeof TASKS_RTK_TAGS];
      id: string;
    }>
  ).concat(
    'url' in instance && instance.url != null ? [{type, id: instance.url}] : []
  );
}

function getAssignedTaskNotification(
  task: TaskTemplateSerializedType,
  message?: (users: string) => string
): [string, ToastMessageOptionsType] {
  const users = toOxfordCommaList(
    [...task.users, ...task.teams, ...task.roles].map((item) => item.name)
  );

  return [
    message != null
      ? message(users)
      : gt('Task assigned to %(users)s.', {users}),
    {
      link: {
        text: gt('View task'),
        to: task.html_url,
      },
      timeout: 8000,
    },
  ];
}

export interface TaskTemplateFetchType {
  object_id: string | number;
  content_type: TaskAssignableContentTypes;
  page_size: number;
  page?: number;
}
